import React, { useCallback, useState } from 'react';

import { Alert, Box } from '@mui/material';
import { DataGrid, GridRowSelectionModel } from '@mui/x-data-grid';

import {
  DEPARTURE_INITIAL_SEARCH,
  DepartureCommittedSearch,
  DepartureSearchState,
  departureColumns,
} from '~/components/Cruises/components/shared/DepartureGridConfig';
import DepartureSearchForm from '~/components/Cruises/components/shared/DepartureSearchForm';
import { Port } from '~/components/Cruises/pages/Ports/types';

import { DEPARTURE_PAGE_SIZE, useGetDepartures } from '~/hooks/apis/cruises/useGetDepartures';

const PAGE_SIZE = DEPARTURE_PAGE_SIZE;

interface Props {
  vendorId: string | undefined;
  vendorName: string | undefined;
  selectedDepartureIds: Array<string>;
  existingDepartureIds?: Array<string>;
  onSelectionChange: (selectedIds: Array<string>) => void;
}

export default function DepartureFilterTab({
  vendorId,
  vendorName,
  selectedDepartureIds,
  existingDepartureIds = [],
  onSelectionChange,
}: Props) {
  const [searchFields, setSearchFields] = useState<DepartureSearchState>(DEPARTURE_INITIAL_SEARCH);
  const [selectedPorts, setSelectedPorts] = useState<Array<Port>>([]);
  const [committedSearch, setCommittedSearch] = useState<DepartureCommittedSearch | null>(null);
  const [page, setPage] = useState(0);

  const { data, isFetching, isError } = useGetDepartures(committedSearch, page);

  const rows = data?.result ?? [];
  const total = data?.total ?? 0;

  const handleSearch = useCallback(() => {
    if (!vendorId) {
      return;
    }
    setPage(0);
    setCommittedSearch({
      ...searchFields,
      vendorId,
      departurePortExternalIds: selectedPorts.map((p) => p.externalId).filter(Boolean),
    });
  }, [searchFields, vendorId, selectedPorts]);

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === 'Enter') {
        handleSearch();
      }
    },
    [handleSearch],
  );

  const handleSelectionChange = useCallback(
    (model: GridRowSelectionModel) => {
      const ids = Array.isArray(model) ? model.map(String) : [];
      onSelectionChange(ids.filter((id) => !existingDepartureIds.includes(id)));
    },
    [onSelectionChange, existingDepartureIds],
  );

  if (!vendorId) {
    return (
      <Alert severity="info" sx={{ mt: 2 }}>
        No cruise line associated with this inclusion. Departure filters cannot be added.
      </Alert>
    );
  }

  return (
    <Box>
      <DepartureSearchForm
        searchFields={searchFields}
        onSearchFieldsChange={setSearchFields}
        onSearch={handleSearch}
        isSearching={isFetching}
        onKeyDown={handleKeyDown}
        vendorName={vendorName}
        selectedPorts={selectedPorts}
        onPortsChange={setSelectedPorts}
      />

      {isError && (
        <Alert severity="error" sx={{ mb: 2 }}>
          Failed to load departures. Please try again.
        </Alert>
      )}

      {!committedSearch ? (
        <Alert severity="info">Use the filters above and click Search to find departures.</Alert>
      ) : (
        <DataGrid
          loading={isFetching}
          rows={rows}
          columns={departureColumns}
          getRowId={(row) => row.id}
          checkboxSelection
          rowSelectionModel={[...existingDepartureIds, ...selectedDepartureIds]}
          onRowSelectionModelChange={handleSelectionChange}
          isRowSelectable={(params) => !existingDepartureIds.includes(params.id as string)}
          keepNonExistentRowsSelected
          disableRowSelectionOnClick={false}
          pagination
          paginationMode="server"
          rowCount={total}
          pageSizeOptions={[PAGE_SIZE]}
          paginationModel={{ page, pageSize: PAGE_SIZE }}
          onPaginationModelChange={({ page: newPage }) => setPage(newPage)}
          autoHeight
          disableColumnMenu
        />
      )}
    </Box>
  );
}
